import {
  ButtonHTMLAttributes,
  MouseEventHandler,
  ReactNode,
  useState,
} from "react";

import {
  faCheck,
  faChevronDown,
  faCircleNotch,
  IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { twJoin, twMerge } from "tailwind-merge";

import { Dropdown, DropdownItem } from "src/design-system/Dropdown";
import { Icon } from "src/design-system/Icon";
import { assertUnreachable } from "src/utils/typeUtils";

export type ButtonVariant = "primary" | "secondary" | "warning";

export type ButtonProps = {
  variant?: ButtonVariant;
  isLoading?: boolean;
  children?: ReactNode;
  iconLeft?: IconDefinition;
  iconRight?: IconDefinition;
  isDisabled?: boolean;
  size?: "sm" | "md";
  dataLoc?: string;
  htmlType?: ButtonHTMLAttributes<HTMLButtonElement>["type"];
  onClick?: MouseEventHandler<HTMLButtonElement>;
  fullWidth?: boolean;
  tabIndex?: number;
  id?: string;
  "aria-label"?: string;
  ref?: React.Ref<HTMLButtonElement>;
};

const baseClasses = "group gap-1.5 antialiased";
const baseSizeClasses = "h-8 font-inter-medium-13px rounded-lg";
const sizeClasses = {
  sm: "px-2.5 py-1 h-6 font-inter-medium-12px rounded-md",
  md: twJoin("px-4 py-2", baseSizeClasses),
};
const solidDisableClasses =
  "bg-gray-100 text-gray-500 cursor-not-allowed shadow-none";
const outlineDisableClasses =
  "bg-white text-gray-400 cursor-not-allowed shadow-none border border-gray-300";
const iconBaseClass =
  "flex justify-center text-[10px] h-2.5 w-2.5 items-center text-center text-gray-600";

const resolveVariantClasses = (
  variantClasses: string,
  disabledClasses: string,
  disabled: boolean,
) => twJoin(!disabled && variantClasses, disabled && disabledClasses);

const resolveButtonClasses = (
  variant: "primary" | "secondary" | "warning",
  disabled: boolean,
) => {
  switch (variant) {
    case "primary":
      return resolveVariantClasses(
        "bg-indigo-600 text-white shadow-sm hover:bg-indigo-700 hover:text-white shadow-sm",
        solidDisableClasses,
        disabled,
      );
    case "secondary":
      return resolveVariantClasses(
        "bg-white text-gray-800 border border-gray-300 shadow-sm",
        outlineDisableClasses,
        disabled,
      );
    case "warning":
      return resolveVariantClasses(
        "bg-red-600 text-white hover:bg-red-700 shadow-sm",
        solidDisableClasses,
        disabled,
      );
    default:
      assertUnreachable(variant);
  }
};

/**
 * Icons of primary buttons have a different color when not on hover or when disabled.
 */
const resolveIconClasses = (
  variant: "primary" | "secondary" | "warning",
  disabled: boolean,
) => {
  if (variant === "primary" || variant === "warning") {
    return twMerge(
      iconBaseClass,
      !disabled && "text-white group-hover:text-white",
      disabled && "text-gray-400",
    );
  } else {
    return twMerge(iconBaseClass, disabled && "text-gray-400");
  }
};

const ButtonSpinner: React.FC<{
  variant: ButtonVariant;
  disabled: boolean;
}> = ({ variant, disabled }) => (
  <Icon
    icon={faCircleNotch}
    size="xs"
    spin
    variant={disabled || variant === "secondary" ? "default" : "inverted"}
  />
);

export const Button: React.FC<ButtonProps> = ({
  children,
  dataLoc,
  isDisabled = false,
  fullWidth = false,
  htmlType = "button",
  iconLeft,
  iconRight,
  isLoading = false,
  onClick,
  size = "md",
  variant = "primary",
  tabIndex,
  id,
  "aria-label": ariaLabel,
  ref,
}) => {
  const buttonSizeClasses = sizeClasses[size] || sizeClasses.md;
  const buttonTypeClasses = resolveButtonClasses(variant, isDisabled);
  const iconClasses = resolveIconClasses(variant, isDisabled);
  const contentVisibilityClasses = twJoin(
    "transition-opacity duration-200",
    isLoading ? "invisible opacity-0" : "opacity-100",
  );

  return (
    <button
      ref={ref}
      aria-label={ariaLabel}
      className={twJoin(
        fullWidth && "w-full",
        "relative flex items-center justify-center focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/25",
        baseClasses,
        buttonSizeClasses,
        buttonTypeClasses,
      )}
      data-loc={dataLoc}
      disabled={isDisabled || isLoading}
      id={id}
      tabIndex={tabIndex}
      type={htmlType}
      onClick={onClick}
    >
      {iconLeft && (
        <span className={twJoin(iconClasses, contentVisibilityClasses)}>
          <FontAwesomeIcon icon={iconLeft} />
        </span>
      )}
      <span className={contentVisibilityClasses} data-record="true">
        {children}
      </span>
      {iconRight && (
        <span className={twJoin(iconClasses, contentVisibilityClasses)}>
          <FontAwesomeIcon icon={iconRight} />
        </span>
      )}
      {isLoading && (
        <div className="absolute">
          <ButtonSpinner disabled={isDisabled} variant={variant} />
        </div>
      )}
    </button>
  );
};

export type SplitButtonOption = {
  key: string;
  label: ReactNode;
  onClick: () => void;
  isDisabled?: boolean;
};

export type SplitButtonVariant = "primary" | "secondary";

export type SplitButtonProps = {
  options: SplitButtonOption[];
  defaultOptionKey?: string;
  variant?: SplitButtonVariant;
  iconLeft?: IconDefinition;
  isLoading?: boolean;
  isDisabled?: boolean;
  dataLoc?: string;
};

const splitClasses = {
  container: baseSizeClasses,
  // Outer paddings stay on the segments, not the container: they must be
  // part of the buttons' click area, or the edges become dead zones.
  action: "gap-1.5 pl-4 pr-2",
  trigger: "pl-3 pr-4",
  divider: "h-4",
} as const;

const resolveDividerClasses = (
  variant: SplitButtonVariant,
  disabled: boolean,
) => {
  if (disabled) return "bg-gray-300";
  switch (variant) {
    case "primary":
      return "bg-indigo-400";
    case "secondary":
      return "bg-gray-200";
    default:
      assertUnreachable(variant);
  }
};

/**
 * Button with a main action and a menu to switch that action.
 * The menu only changes the selection, the main segment runs it.
 */
export const SplitButton: React.FC<SplitButtonProps> = ({
  options,
  defaultOptionKey,
  variant = "primary",
  iconLeft,
  isLoading = false,
  isDisabled = false,
  dataLoc,
}) => {
  const [selectedKey, setSelectedKey] = useState(
    defaultOptionKey ?? options[0]?.key,
  );
  const selectedOption =
    options.find((option) => option.key === selectedKey) ?? options[0];
  const inactive = isDisabled || isLoading;

  const iconClasses = resolveIconClasses(variant, isDisabled);
  const segmentCursorClasses = inactive && "cursor-not-allowed";

  return (
    <Menu as="div" className="relative inline-block" data-loc={dataLoc}>
      <div
        className={twJoin(
          "group relative flex items-stretch antialiased",
          "focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-500/25",
          splitClasses.container,
          resolveButtonClasses(variant, isDisabled),
        )}
      >
        <button
          className={twJoin(
            "flex items-center focus:outline-none",
            splitClasses.action,
            segmentCursorClasses,
          )}
          data-loc={dataLoc ? `${dataLoc}-button` : undefined}
          disabled={inactive}
          type="button"
          onClick={selectedOption?.onClick}
        >
          {iconLeft && (
            <span className={twJoin(iconClasses, isLoading && "invisible")}>
              <FontAwesomeIcon icon={iconLeft} />
            </span>
          )}
          <span
            className={twJoin("whitespace-nowrap", isLoading && "invisible")}
            data-record="true"
          >
            {selectedOption?.label}
          </span>
        </button>
        <div
          aria-hidden
          className={twJoin(
            "w-px self-center",
            splitClasses.divider,
            resolveDividerClasses(variant, isDisabled),
            isLoading && "invisible",
          )}
        />
        <MenuButton
          aria-label="Select action"
          className={twJoin(
            "flex items-center focus:outline-none",
            splitClasses.trigger,
            segmentCursorClasses,
          )}
          data-loc={dataLoc ? `${dataLoc}-menu-button` : undefined}
          disabled={inactive}
        >
          <span className={twJoin(iconClasses, isLoading && "invisible")}>
            <FontAwesomeIcon icon={faChevronDown} />
          </span>
        </MenuButton>
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <ButtonSpinner disabled={isDisabled} variant={variant} />
          </div>
        )}
      </div>
      <MenuItems
        anchor={{ to: "bottom end", gap: 8 }}
        as={Dropdown}
        className="w-auto outline-none"
        data-loc={dataLoc ? `${dataLoc}-dropdown` : undefined}
        transition
      >
        {options.map((option) => (
          <MenuItem
            key={option.key}
            as={DropdownItem}
            data-loc={dataLoc ? `${dataLoc}-option-${option.key}` : undefined}
            disabled={option.isDisabled}
            onClick={() => setSelectedKey(option.key)}
          >
            <span>{option.label}</span>
            {option.key === selectedKey && (
              <Icon icon={faCheck} size="md" variant="active" />
            )}
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
};
